import { Color, Vector2 } from 'three';
import { isCameraVisible, projectToScreen } from '../../Utils';
/**
 * Framework for rendering text labels in the 3D space
 */
export class LabelManager {
    constructor(renderer, draw2D) {
        Object.defineProperty(this, "camera", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "draw2D", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "labels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.camera = renderer.getWorld().getCamera();
        this.draw2D = draw2D;
        this.labels = new Map();
    }
    /**
     * Create a new text label to be drawn, returning a reference to the
     * modifiable properties of the label
     *
     * @param string
     * @param position
     * @param center
     * @param screenOffset
     * @returns Label reference
     */
    add(string, position, center = false, screenOffset = new Vector2()) {
        const font = 'Helvetica';
        const size = 13;
        const { width, height } = this.draw2D.getCanvas();
        const canvasSize = new Vector2(width, height);
        // Calculate 2D position
        const projected = projectToScreen(this.camera, canvasSize, position);
        projected.x += screenOffset.x;
        projected.y += screenOffset.y;
        // Text size
        const dimensions = new Vector2(this.draw2D.textWidth(string, font, size), this.draw2D.textHeight(size));
        const opacity = isCameraVisible(this.camera, position) ? 1.0 : 0.0;
        const text = {
            type: 'text',
            position: projected,
            font: 'Helvetica',
            string,
            size,
            color: new Color(1, 1, 1),
            opacity,
            fill: true,
            center,
        };
        const backgroundDimensions = new Vector2(1.2, 1.1).multiply(dimensions);
        const backgroundPosition = center
            ? projected
            : projected.clone().sub(new Vector2(0.1, 0.05).multiply(dimensions));
        const background = {
            type: 'rect',
            dimensions: backgroundDimensions,
            position: backgroundPosition,
            color: new Color(0, 0, 0),
            opacity: opacity * 0.75,
            fill: true,
            center,
            radius: dimensions.y * 0.15,
        };
        const label = {
            position,
            screenOffset,
            opacity: 1.0,
        };
        this.labels.set(label, { text, background });
        this.draw2D.addDrawable(background);
        this.draw2D.addDrawable(text);
        return label;
    }
    /**
     * Remove a label, if it exists
     *
     * @param label
     */
    remove(label) {
        const drawables = this.labels.get(label);
        if (drawables) {
            const { text, background } = drawables;
            this.draw2D.removeDrawable(text);
            this.draw2D.removeDrawable(background);
            this.labels.delete(label);
        }
    }
    /**
     * Main update loop to test if the label should be rendered or not
     */
    update() {
        for (const [label, drawables] of this.labels) {
            const { text, background } = drawables;
            const { position, screenOffset, opacity } = label;
            const { center } = background;
            const { width, height } = this.draw2D.getCanvas();
            const canvasSize = new Vector2(width, height);
            // Update screen space position
            const projected = projectToScreen(this.camera, canvasSize, position);
            projected.add(screenOffset);
            const dimensions = new Vector2(this.draw2D.textWidth(text.string, text.font, text.size), this.draw2D.textHeight(text.size));
            text.position = projected;
            background.position = center
                ? projected
                : projected.clone().sub(new Vector2(0.1, 0.05).multiply(dimensions));
            // If the text is behind the 3D camera, hide it
            if (isCameraVisible(this.camera, position)) {
                text.opacity = opacity;
                background.opacity = opacity * 0.75;
            }
            else {
                text.opacity = 0;
                background.opacity = 0;
            }
        }
    }
}
